// ─────────────────────────────────────────────────────────────────────────────
// stores/invitationStore.ts — DIE OFFENEN EINLADUNGEN (T-M30.6)
//
// Spec: REQ-DM-071 (der Feldvertrag), REQ-SEC-029 (a) (`invitations` ist in den
// Rules NUR-ADMIN, lesend wie schreibend), REQ-SEC-030 (e) (erneut einladen),
// REQ-SEC-021 (kein Geheimnis in der Oberflaeche), REQ-OPS-053 (der Fehlerweg).
//
// ── DIESER STORE LIEST NUR ─────────────────────────────────────────────────
// Geschrieben wird `invitations` ausschliesslich von `inviteuser` und
// `acceptinvitation` ueber das Admin SDK (REQ-DM-071 (c)); **hier steht kein
// einziger Schreibweg**, und „erneut einladen" ist deshalb kein Schreibvorgang
// dieses Stores, sondern ein weiterer Aufruf von `inviteuser`
// (`userStore.inviteUser`, REQ-SEC-030 (e): neues Geheimnis, neue Frist, am
// SELBEN Dokument).
//
// ── DAS ABO IST OHNE ADMIN EIN NO-OP (Bauform aus `userStore.subscribe`) ───
// Die Rules erlauben die Sammlung ausschliesslich einem Admin. Ein Abo waere
// bei jedem Kundenbenutzer ein sicheres `permission-denied` — fuenfmal
// wiederaufgenommen, mit einer Fehlermeldung im Rahmen-Hinweis, obwohl nichts
// gestoert ist. **Der Store abonniert gar nicht erst**; die Liste bleibt leer,
// ohne Fehlzustand. *Das ist Bequemlichkeit und keine Sperre — durchgesetzt
// wird in den Rules (REQ-SEC-029).*
//
// ⚠ **DER HASH VERLAESST DIE CLOUD NICHT IN DIE OBERFLAECHE** (REQ-SEC-021,
//   REQ-SEC-030 (c)). Das Dokument fuehrt `tokenHash`; ein Admin DARF ihn nach
//   den Rules lesen — **dieser Store bildet ihn trotzdem nicht ab.** Wer den
//   Hash hat, hat den Vergleichswert; ein Wert, der in keiner Ansicht gebraucht
//   wird, hat im Zustand der Anwendung nichts zu suchen. Der KLARTEXT existiert
//   ohnehin nur im Augenblick des Versands und steht in keinem Dokument.
//
// ── DIE ABFRAGEFORM, und warum sie keinen Index braucht ───────────────────
// Ein Abo auf die ganze Sammlung — **ohne `where`, ohne `orderBy`**. Sortiert
// wird im Browser. Grund: die Rules geben einem Admin die Sammlung als Ganzes
// (REQ-SEC-029 (a)), ein Nicht-Admin abonniert gar nicht, und eine Abfrage ohne
// Filter und ohne Sortierung braucht keinen zusammengesetzten Index
// (REQ-DM-050a). *Damit entsteht mit diesem Task keine neue Abfrageform, deren
// Index vor ihrem Leser hinausgehen muesste (REQ-OPS-039).*
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { collection, type QuerySnapshot, type Unsubscribe } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { liveAbo } from '@/utils/liveAbo';
import { useAuthStore } from './authStore';

/** Der feste Sammlungsname (REQ-OV-008, REQ-DM-071). */
export const INVITATIONS_COLLECTION = 'invitations';

/**
 * Eine Einladung, so weit die Oberflaeche sie kennt.
 *
 * ⚠ **`tokenHash` FEHLT ABSICHTLICH** — siehe den Kopf dieser Datei.
 */
export interface Invitation {
  id:          string;
  email:       string;
  companyIds:  string[];
  role:        'admin' | 'user';
  /** Technisch-englisch (REQ-OV-002, REQ-DM-071): `pending` | `accepted` | `revoked`. */
  status:      string;
  /** Die Frist in Millisekunden — `null`, wenn das Dokument keine fuehrt. */
  expiresAtMs: number | null;
  invitedBy:   string;
}

/** Ein Firestore-Zeitstempel, eine Zahl oder gar nichts → Millisekunden. */
function zeitstempelMs(wert: unknown): number | null {
  if (wert && typeof (wert as { toMillis?: () => number }).toMillis === 'function') {
    return (wert as { toMillis: () => number }).toMillis();
  }
  if (typeof wert === 'number' && Number.isFinite(wert)) return wert;
  return null;
}

/** Nur Zeichenketten, und nur nicht-leere (dieselbe Strenge wie `utils/anspruch.ts`). */
function kennungen(wert: unknown): string[] {
  if (!Array.isArray(wert)) return [];
  return wert.filter((k): k is string => typeof k === 'string' && k.length > 0);
}

export const useInvitationStore = defineStore('invitations', () => {
  const invitations  = ref<Invitation[]>([]);
  const isLoading    = ref(false);
  const error        = ref<string | null>(null);
  const isSubscribed = ref(false);
  let   unsub: Unsubscribe | null = null;

  /** Die offenen Einladungen — der einzige Zustand, den die Oberflaeche zeigt. */
  const offene = computed(() => invitations.value.filter(e => e.status === 'pending'));

  /**
   * Die offene Einladung zu einer Adresse (REQ-DM-071 (a): es gibt hoechstens
   * eine). Die Adresse wird wie in der Cloud normalisiert — getrimmt und
   * kleingeschrieben —, sonst faende `A@b.ch` seine eigene Einladung nicht.
   */
  function offeneFuer(email: string | null | undefined): Invitation | null {
    const gesucht = (email ?? '').trim().toLowerCase();
    if (!gesucht) return null;
    return offene.value.find(e => e.email === gesucht) ?? null;
  }

  function subscribe(): void {
    if (!useAuthStore().isAdmin) {
      unsub?.();
      unsub              = null;
      invitations.value  = [];
      isLoading.value    = false;
      error.value        = null;
      isSubscribed.value = false;
      return;
    }
    // Die vorige Anmeldung wird vor dem Neuabonnieren abgeraeumt (und damit eine
    // laufende Staffel abgebrochen, REQ-OPS-053 (d)) — dieselbe Bauform wie in
    // den uebrigen Stores.
    unsub?.();
    unsub = null;
    isLoading.value    = true;
    error.value        = null;
    isSubscribed.value = true;
    unsub = liveAbo<QuerySnapshot>({
      collection: INVITATIONS_COLLECTION,
      ziel: () => collection(db, INVITATIONS_COLLECTION),
      onData: (snap) => {
        invitations.value = snap.docs.map(d => ({
          id:          d.id,
          email:       (d.data().email as string) ?? '',
          companyIds:  kennungen(d.data().companyIds),
          role:        d.data().role === 'admin' ? 'admin' : 'user',
          status:      (d.data().status as string) ?? 'pending',
          expiresAtMs: zeitstempelMs(d.data().expiresAt),
          invitedBy:   (d.data().invitedBy as string) ?? '',
        }));
        isLoading.value = false;
      },
      onFehler: (err) => {
        error.value        = err.message;
        isLoading.value    = false;
        isSubscribed.value = false;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung): sonst bliebe die Meldung
      // stehen, waehrend die Liste laengst wieder live ist.
      onErholt: () => {
        error.value        = null;
        isSubscribed.value = true;
      },
      onStill: () => {
        isLoading.value    = false;
        isSubscribed.value = false;
      },
    });
  }

  function unsubscribe(): void {
    unsub?.(); unsub = null;
    isSubscribed.value = false;
  }

  return {
    invitations, offene, isLoading, error, isSubscribed,
    subscribe, unsubscribe, offeneFuer,
  };
});
